import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';
import FormTextField from './FormTextField';
import InlineMessage from '@atlaskit/inline-message';
import { gridSize } from '@atlaskit/theme';
import FieldGroup from './FieldGroup';
import TallButton from './TallButton';
import messages from './RecoveryForm.messages';
import { MultifactorMethod, MultiFactorRecoveryError } from '../types';
import { SecurityWall } from './SecurityWall';
import {
  InlineMessageContainer,
  InputMessage,
  StyledMessage,
} from '../containers/MultiFactorPage.helpers';
import { usePrevious } from '../utilities/hooks';

const ButtonContainer = styled.div`
  margin-top: ${gridSize()}px;
`;

const MessageWrapper = styled.div`
  padding-bottom: ${gridSize() * 1.5}px;
`;

type RecoveryCodeFieldError = MultiFactorRecoveryError | { errorCode: 'invalid_otp_pattern' };

interface Props {
  disabled: boolean;
  fieldErrorFromParent: MultiFactorRecoveryError | null | undefined;
  onChange: (props: { target: HTMLInputElement | null }) => void;
}

const RecoveryCodeField = ({ disabled, fieldErrorFromParent, onChange }: Props) => {
  const { formatMessage } = useIntl();

  const fieldTextRef = useRef<HTMLInputElement>(null);
  const [fieldValue, setFieldValue] = useState<string>('');
  const [fieldError, setFieldError] = useState<RecoveryCodeFieldError | null | undefined>(
    fieldErrorFromParent
  );
  const previousFieldValue = usePrevious(fieldValue);
  const previousFieldErrorFromParent = usePrevious(fieldErrorFromParent);

  const handlePaste = (e: ClipboardEvent) => {
    const target: HTMLInputElement = e.target as HTMLInputElement;
    const selectionStart = target.selectionStart || 0;
    const selectionEnd = target.selectionEnd || target.value.length;
    const start = target.value.substr(0, selectionStart);
    const middle = (e.clipboardData || window.clipboardData)
      .getData('text/plain')
      .replace(/\W/g, '')
      .substr(0, target.maxLength - target.value.length - selectionStart + selectionEnd);
    const end = target.value.substr(selectionEnd);
    //timeout to allow input update UI
    setTimeout(() => setFieldValue(start + middle + end));
  };

  const handleChange = (e: SyntheticEvent<HTMLInputElement>) => {
    setFieldValue(e.currentTarget.value.replace(/\W/g, '').toUpperCase());
    setFieldError(null);
  };

  const handleInvalid = (e: Event) => {
    e.preventDefault();
    setFieldError({
      errorCode: 'invalid_otp_pattern',
    });
  };

  useEffect(() => {
    const input = fieldTextRef.current;
    if (input) {
      input.addEventListener('invalid', handleInvalid);
      input.addEventListener('paste', handlePaste);
    }
    return () => {
      if (input) {
        input.removeEventListener('invalid', handleInvalid);
        input.removeEventListener('paste', handlePaste);
      }
    };
  }, []);

  useEffect(() => {
    if (!previousFieldErrorFromParent && fieldErrorFromParent) {
      setFieldError(fieldErrorFromParent);
      fieldTextRef.current && fieldTextRef.current.select();
    }
  }, [fieldErrorFromParent, previousFieldErrorFromParent]);

  useEffect(() => {
    if (fieldValue !== previousFieldValue) {
      onChange &&
        onChange({
          target: fieldTextRef.current,
        });
    }
  }, [fieldValue, previousFieldValue, onChange]);

  const errorContent = (error: RecoveryCodeFieldError) => {
    const errorMessages = {
      invalid_recovery_code: formatMessage(messages.errorsInvalidRecoverCode),
      invalid_recovery_code_format: formatMessage(messages.errorsInvalidRecoveryCodeFormat),
      field_required: formatMessage(messages.errorsNoRecoveryCode),
      invalid_token: (
        <form onSubmit={() => window.location.reload()}>
          <FormattedMessage
            id="mf.mfform.errors.invalid_token"
            defaultMessage="Your session has expired. Please refresh the page and try again."
          />
          <ButtonContainer>
            <TallButton type="submit" isFullWidth>
              <FormattedMessage id="common.refresh" defaultMessage="Refresh" />
            </TallButton>
          </ButtonContainer>
        </form>
      ),
      invalid_otp_pattern: formatMessage(messages.errorsInvalidOtpPattern),
    };

    return (
      error &&
      // @ts-ignore genuine TypeScript screw up, need to turn RecoveryCodeFieldError into a genuine discriminated union
      (errorMessages[error.errorCode] || (error.message && typeof error.message === 'string'))
    );
  };

  return (
    <FieldGroup>
      <FormTextField
        name="recoveryCode"
        type="text"
        label={formatMessage(messages.mfRecoveryFormPlaceholder)}
        autoFocus
        onChange={handleChange}
        isInvalid={!!fieldError}
        invalidMessage={!disabled && fieldError && errorContent(fieldError)}
        value={fieldValue}
        isDisabled={disabled}
        pattern="\w{24}"
        maxLength={24}
        isRequired
        ref={fieldTextRef}
      />
    </FieldGroup>
  );
};

interface RecoveryFormProps {
  recoveryError: MultiFactorRecoveryError | null | undefined;
  isLoading: boolean;
  multifactorMethod?: MultifactorMethod;
  recoverAccount: (string: string) => void;
}

const RecoveryForm = ({
  isLoading,
  recoverAccount,
  multifactorMethod,
  recoveryError,
}: RecoveryFormProps) => {
  const { formatMessage } = useIntl();

  const handleInputChange = (e: { target: HTMLInputElement | null }) => {
    const target = e.target;
    if (target && target.value.length === target.maxLength) {
      recoverAccount(target.value);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // @ts-ignore check that recoveryCode is actually on the target
    recoverAccount(e.target.recoveryCode.value);
  };

  return (
    <SecurityWall id="two-step-verification-recovery-form">
      <InputMessage>
        <MessageWrapper>
          {multifactorMethod === 'security-key' ? (
            <FormattedMessage
              id="mf.recoveryform.usekey.securitykey"
              defaultMessage="If you can't use your security key, log in with your emergency recovery key."
            />
          ) : (
            <FormattedMessage
              id="mf.recoveryform.usekey"
              defaultMessage="If you can't use your phone, log in with your emergency recovery key."
            />
          )}
        </MessageWrapper>
        <InlineMessageContainer>
          <InlineMessage
            appearance="info"
            placement="bottom-end"
            iconLabel={formatMessage({
              id: 'mf.recoveryform.help.inline-message.button.label',
              defaultMessage: 'More info',
              description: 'Icon button label for the inline message',
            })}>
            <StyledMessage>
              <FormattedMessage
                id="mf.recoveryform.help"
                defaultMessage="We asked you to save your emergency recovery key when you enabled two-step verification. You might have saved it in a local file or printed it out."
              />
            </StyledMessage>
          </InlineMessage>
        </InlineMessageContainer>
      </InputMessage>
      <form autoComplete="off" onSubmit={handleFormSubmit}>
        <RecoveryCodeField
          disabled={isLoading}
          fieldErrorFromParent={recoveryError}
          onChange={handleInputChange}
        />
        <ButtonContainer id="two-step-verification-recovery-submit">
          <TallButton appearance="primary" type="submit" isFullWidth isLoading={isLoading}>
            <FormattedMessage id="common.login" defaultMessage="Log in" />
          </TallButton>
        </ButtonContainer>
      </form>
    </SecurityWall>
  );
};

export default RecoveryForm;
